import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import HeroSection from '../components/home/HeroSection';
import WhyChooseUs from '../components/home/WhyChooseUs';
import ServicesSummary from '../components/home/ServicesSummary';
import EnterprisePackagesSection from '../components/home/EnterprisePackagesSection';
import HowItWorks from '../components/home/HowItWorks';
import packageService from '../services/packageService';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Home: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Define the interface for packages used by EnterprisePackagesSection
  interface PackageCardData {
    id: string;
    name: string;
    price: string;
    features: string[];
    description: string;
    popular: boolean;
  }

  const [packages, setPackages] = useState<Record<string, PackageCardData[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPackages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]); // Reload when language changes

  const loadPackages = async () => {
    try {
      setLoading(true);
      setError('');

      // Load all packages grouped by slug format
      const groupedPackages = await packageService.getAllPackagesGroupedBySlug();
      const packagesData: Record<string, PackageCardData[]> = {};

      // Transform packages to match the expected format for PackageCards
      Object.keys(groupedPackages).forEach(slug => {
        packagesData[slug] = groupedPackages[slug].map(pkg => {
          // Use Arabic data if language is Arabic and Arabic data exists
          const name = i18n.language === 'ar' && pkg.name_ar ? pkg.name_ar : pkg.name;
          const description = i18n.language === 'ar' && pkg.description_ar ? pkg.description_ar : (pkg.description || '');
          const features = i18n.language === 'ar' && pkg.features_ar && pkg.features_ar.length > 0
            ? pkg.features_ar.filter(f => f && f.trim() !== '')
            : (Array.isArray(pkg.features) ? pkg.features : []);

          return {
            id: pkg.id.toString(),
            name,
            price: packageService.formatPrice(pkg),
            features,
            description,
            popular: pkg.popular
          };
        });
      });

      setPackages(packagesData);
    } catch (err) {
      console.error('Error loading packages:', err);
      setError(t('errors.failedToLoad'));
      // Fallback to empty packages
      setPackages({});
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <HeroSection />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-center items-center py-20">
            <LoadingSpinner />
          </div>
        </div>
        <WhyChooseUs />
        <ServicesSummary />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <HeroSection />
        <WhyChooseUs />
        <ServicesSummary />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center py-20">
            <div className="text-red-600 mb-4">⚠️ {t('errors.failedToLoad')}</div>
            <button
              onClick={loadPackages}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
            >
              {t('errors.tryAgain')}
            </button>
          </div>
        </div>
        <HowItWorks />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <HeroSection />
      <WhyChooseUs />
      <ServicesSummary />
      <EnterprisePackagesSection allPackages={packages} />
      <HowItWorks />
    </div>
  );
};

export default Home;